/**
 * Het contract tussen API en scherm: **de frontend rekent nooit.**
 *
 * Geen ratio's, geen indexatie, geen afronding van bedragen, geen
 * percentageberekening, geen valutaopmaak in JavaScript. Elk getal dat een
 * gebruiker ziet komt kant-en-klaar uit de API — inclusief scheidingstekens,
 * eenheid, teken en het aantal decimalen. De rekenkern is de enige plek waar
 * een getal ontstaat (`atlas-rekenkern`: de computer rekent, de AI legt uit).
 *
 * Waarom zo streng: dezelfde beleidswaarde mag niet twee gedaanten hebben
 * omdat een tegel op duizendtallen afrondt en een tabel op eenheden. En een
 * ratio die in JavaScript wordt nagerekend, is per definitie niet de ratio die
 * in de vastgelegde `CalculationRun` staat.
 *
 * Daarom draagt elke waarde in dit model een `display`: precies die tekst gaat
 * naar het scherm. Het optionele `plot` bestaat alleen voor geometrie —
 * staafhoogtes, donutsegmenten, sparklinepaden — en voor sorteervolgorde. Dat
 * is vormgeving, geen rekenwerk. `plot` wordt nooit getoond en nooit met een
 * andere waarde gecombineerd.
 */

/** Een getal zoals de API het aanlevert: al opgemaakt. */
export type DisplayValue = {
  /** Wat het scherm toont. Onbewerkt, letterlijk, inclusief eenheid. */
  display: string;
  /**
   * Uitsluitend voor grafiekgeometrie en sorteervolgorde. Nooit tonen, nooit
   * optellen, nooit delen. `null` betekent: niet plotbaar (bv. "n.v.t.").
   */
  plot?: number | null;
  /** Korte toelichting, bijvoorbeeld de eenheid of de norm. */
  hint?: string;
  /**
   * Een klein teken naast de waarde, met de uitleg in de tooltip. Voor een
   * signaal dat de waarde zelf niet verandert — bijvoorbeeld een energielabel
   * dat afwijkt van de EP-Online-registratie. De API bepaalt óf en welk teken
   * er staat; het scherm bedenkt er geen bij.
   */
  flag?: {
    mark: string;
    label: string;
    hint: string;
    /** Een lijst met alle objecten met hetzelfde signaal (FB-056). */
    href?: string | null;
  };
};

/** Richting van een verschil ten opzichte van de vorige periode. */
export type DeltaDirection = "up" | "down" | "flat";

/**
 * Of die richting gunstig is, weet alleen de rekenkern: een dalende LTV is
 * goed nieuws, een dalende ICR niet. Het scherm kleurt op `sentiment`, niet op
 * `direction`.
 */
export type DeltaSentiment = "positive" | "negative" | "neutral";

export type Delta = {
  /** Opgemaakt verschil, bijvoorbeeld "+3,4%" of "− 0,08". */
  display: string;
  direction: DeltaDirection;
  sentiment: DeltaSentiment;
  /** Waarmee is vergeleken, bijvoorbeeld "t.o.v. 31-12-2024". */
  label: string;
};

/**
 * Reeks voor een sparkline. Alleen geometrie: de punten hebben geen betekenis
 * los van hun onderlinge verhouding en worden nergens als getal getoond.
 */
export type Sparkline = {
  points: number[];
  /** Wat de lijn voorstelt; gaat naar de schermlezer. */
  label: string;
};

/** Herkomst van wat er op het scherm staat. Hoort bij élk cijfer. */
export type ProvenanceBasis = "officieel" | "synthetisch";

export type Provenance = {
  /** Bronsysteem of -register, bijvoorbeeld "WALS/ViewPoint" of "Leidraad 2026". */
  source: string;
  /**
   * De afzonderlijke bronnen als `source` er meer samenvat (de parametersets
   * achter de beleidswaarde). De compacte herkomstregel noemt er dan twee en
   * telt de rest; zie ProvenanceFootnote.
   */
  sources?: string[];
  /** Peildatum, opgemaakt door de API. */
  referenceDate: string;
  /** Id van de vastgelegde CalculationRun, als het cijfer uit een run komt. */
  runId?: string | null;
  /** Versienummer van die run binnen (object, berekeningstype, verslagjaar). */
  runVersion?: string | null;
  /**
   * Aantal vastgelegde runs waar een som over gaat, zoals de beleidswaarde van
   * de portefeuille. Dan is er geen één run-id die het cijfer draagt.
   */
  runCount?: number | null;
  /**
   * `officieel` = gerekend met een gepubliceerde, officiële parameterset.
   * `synthetisch` = demo-, scenario- of fixturewaarde. Nooit voor verantwoording.
   */
  basis: ProvenanceBasis;
  /** Extra duiding, bijvoorbeeld welke parameterset is gebruikt. */
  note?: string;
};

/** Eén ontbrekende voorwaarde uit een fail-closed antwoord (HTTP 409). */
export type MissingRequirement = {
  /** Machinecode uit de API, bijvoorbeeld een parametercode. */
  code?: string;
  /** Wat er ontbreekt, in gewone taal. */
  label: string;
  /** Waar het vandaan zou moeten komen. */
  reference?: string;
};

/** De stap die de blokkade opheft. */
export type ResolutionStep = {
  step: string;
  description: string;
  /** Route binnen de applicatie waar die stap wordt gezet. */
  href?: string;
};

/**
 * Een fail-closed antwoord: het platform weigert te rekenen omdat invoer
 * ontbreekt. Dat is geen fout maar een ontwerpkeuze — er wordt liever niets
 * getoond dan een getal op een aanname.
 */
export type FailClosed = {
  title: string;
  /** WAT er ontbreekt, in één alinea. */
  explanation: string;
  missing: MissingRequirement[];
  /** WELKE stap dat oplost. */
  resolution: ResolutionStep[];
  /** Herleidbaarheid: status en endpoint, voor in een melding aan de beheerder. */
  reference?: string;
};

/**
 * Een gat in de rekenketen: een plek waar het scherm iets zou moeten tonen dat
 * de API (nog) niet levert. Het scherm laat het gat expliciet staan in plaats
 * van het zelf uit te rekenen.
 */
export type CalculationGap = {
  /** Wat er zou moeten staan. */
  expected: string;
  /** Welk veld of endpoint dat zou moeten leveren. */
  awaiting: string;
};

/** Wordt getoond in plaats van een waarde die de API niet levert. */
export const GAP_PLACEHOLDER = "n.v.t.";

/**
 * Waarde die de API niet heeft geleverd. Geen nul, geen streepje dat op nul
 * lijkt: een leeg vakje met een uitleg voor de schermlezer.
 */
export const missingValue = (reason: string): DisplayValue => ({
  display: GAP_PLACEHOLDER,
  plot: null,
  hint: reason,
});

/**
 * Woorden die een toestand zijn en geen uitkomst (ronde 5): "n.v.t.", "aan te
 * leveren", "nieuw", "geen saldo" … Ze krijgen overal dezelfde stijl
 * (`EmptyValue`, klasse `.atl-empty`): de letter en grootte van een waarde op
 * die plek, maar gedempt; nooit vet navy, want dan lezen ze als een uitkomst.
 */
const LEGE_WOORDEN = new Set([
  "n.v.t.",
  "aan te leveren",
  "nieuw",
  "geen saldo",
  "geen doel",
  "niet beschikbaar",
  "nog niet gemeten",
  "nog niet berekend",
  "onbekend",
  "geen",
]);

/** Is deze tekst een lege waarde (een toestand) en geen getal? */
export const isEmptyDisplay = (display: string | null | undefined): boolean =>
  display == null || display.trim() === "" || LEGE_WOORDEN.has(display.trim().toLowerCase());

/** Kan deze waarde in een grafiek? */
export const isPlottable = (value: DisplayValue): value is DisplayValue & { plot: number } =>
  typeof value.plot === "number" && Number.isFinite(value.plot);

/**
 * Een reden van de API ("geen vastgestelde MJOB-reeks voor dit verslagjaar") als
 * volzin: hoofdletter vooraan, punt erachter (ronde 7, van bezit naar het
 * fundament). Alleen de opmaak verandert, niet de tekst. `EmptyState` doet dit
 * zelf met zijn omschrijving, zodat een reden van de API en een eigen zin in
 * een lege staat dezelfde schrijfwijze hebben.
 */
export function alsZin(tekst: string): string {
  const kaal = tekst.trim();
  if (!kaal) return kaal;
  const begin = kaal.charAt(0).toLocaleUpperCase("nl-NL") + kaal.slice(1);
  return /[.!?]$/.test(begin) ? begin : `${begin}.`;
}
